import { LoadingRows } from '@/components/LoadingRows';
import { SearchForm } from '@/components/SearchForm';
import { TablePagination, TableSortControls } from '@/components/TableParts';
import { TwoFactorBadge } from '@/components/TwoFactorBadge';
import { convertToEt } from '@/helpers/convertToET';
import { exportToCSV } from '@/helpers/exportDataToCsv';
import { useAppDispatch, useAppSelector, useErrorIndicator } from '@/hooks';
import { StatusType } from '@/types';
import {
  Button,
  HStack,
  Switch,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { debounce } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { getAllUsersForExport, getUsers, reset2FA, updateUser } from './actions';
import { EditOrgForm } from './EditOrgForm';
import {
  selectLimit,
  selectPagination,
  selectSearchQuery,
  selectSortBy,
  selectSortOrder,
  selectUsers,
  setCurrentPage,
  setLimit,
  setSearchQuery,
  setSortBy,
  setSortOrder,
} from './reducer';
import { Reset2FAModal } from './Reset2FAModal';

export const UserManageView: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isExportLoading, setIsExportLoading] = useState(false);
  const dispatch = useAppDispatch();
  const limit = useAppSelector(selectLimit);
  const users = useAppSelector(selectUsers);
  const pagination = useAppSelector(selectPagination);
  const sortBy = useAppSelector(selectSortBy);
  const sortOrder = useAppSelector(selectSortOrder);
  const searchQuery = useAppSelector(selectSearchQuery);
  const toast = useToast();
  const { showError } = useErrorIndicator();
  const { isOpen: isResetOpen, onOpen: onResetOpen, onClose: onResetClose } = useDisclosure();
  const [selectedUser, setSelectedUser] = useState<{ id: string; email: string } | null>(null);
  const [isResetting, setIsResetting] = useState(false);

  const sortOptions = [
    { value: 'email', label: 'Email' },
    { value: 'timeJoined', label: 'Joined At' },
    { value: 'lastLoggedIn', label: 'Last Login' },
  ];

  const handleSearch = useCallback(
    debounce((value: string) => {
      dispatch(setSearchQuery(value));
    }, 300),
    [dispatch],
  );

  const handleLimitChange = (newLimit: number) => {
    dispatch(setLimit(newLimit));
  };

  const handleStatusChange = async (userId: string, currentStatus: StatusType) => {
    const newStatus =
      currentStatus === 'pending' || currentStatus === 'denied' ? 'approved' : 'denied';
    try {
      await dispatch(
        updateUser({
          userId,
          field: 'accountStatus',
          value: newStatus,
        }),
      ).unwrap();

      toast({
        title: `User status changed to ${newStatus === 'approved' ? 'APPROVED' : 'DENIED'}`,
        status: 'success',
        position: 'top',
        duration: 5000,
      });
    } catch (error) {
      showError('Failed to update status');
    }
  };

  const handleOrgUpdate = async (userId: string, newOrg: string) => {
    await dispatch(
      updateUser({
        userId,
        field: 'organization',
        value: newOrg,
      }),
    ).unwrap();
  };

  const handleReset2FAClick = (userId: string, email: string) => {
    setSelectedUser({ id: userId, email });
    onResetOpen();
  };

  const handleReset2FAConfirm = async () => {
    if (!selectedUser) return;

    setIsResetting(true);
    try {
      await dispatch(reset2FA(selectedUser.id)).unwrap();
      toast({
        title: '2FA Reset',
        description: `Two-factor authentication has been reset for ${selectedUser.email}`,
        status: 'success',
        position: 'top',
        duration: 5000,
      });
      // Refresh the user list
      dispatch(
        getUsers({
          currentPage: pagination.currentPage,
          sortBy,
          sortOrder,
          limit,
          searchQuery,
        }),
      );
    } catch (error) {
      showError('Failed to reset 2FA');
    } finally {
      setIsResetting(false);
      onResetClose();
      setSelectedUser(null);
    }
  };

  const handlePrevPage = () => {
    if (pagination.currentPage > 1) {
      dispatch(setCurrentPage(pagination.currentPage - 1));
    }
  };

  const handleNextPage = () => {
    if (pagination.hasNextPage) {
      dispatch(setCurrentPage(pagination.currentPage + 1));
    }
  };

  const handleSortByChange = (newSortBy: string) => {
    dispatch(setSortBy(newSortBy as 'email' | 'timeJoined' | 'lastLoggedIn'));
  };

  const handleSortOrderChange = (newSortOrder: 'asc' | 'desc') => {
    dispatch(setSortOrder(newSortOrder));
  };

  const handleCsvExport = async () => {
    setIsExportLoading(true);
    try {
      const result = await dispatch(getAllUsersForExport()).unwrap();
      await exportToCSV(result, 'usersManage');
    } catch (error) {
      showError('Failed to export users data');
    } finally {
      setIsExportLoading(false);
    }
  };

  useEffect(() => {
    setIsLoading(true);
    dispatch(
      getUsers({
        currentPage: pagination.currentPage,
        sortBy,
        sortOrder,
        limit,
        searchQuery,
      }),
    ).finally(() => setIsLoading(false));
  }, [dispatch, pagination.currentPage, sortBy, sortOrder, limit, searchQuery]);

  return (
    <VStack mt={5} gap={8}>
      <Button
        fontWeight="semibold"
        variant="primary"
        mt={4}
        maxW="160px"
        height="45px"
        float="right"
        w="full"
        type="button"
        onClick={handleCsvExport}
        isLoading={isExportLoading || isLoading}
      >
        Export to CSV
      </Button>
      <VStack maxW="full">
        <HStack mb={4} flexDir={{ base: 'column', md: 'row' }} gap={6}>
          <SearchForm isLoading={isLoading} onSearch={handleSearch} />
          <TableSortControls
            sortOptions={sortOptions}
            currentSortBy={sortBy}
            currentSortOrder={sortOrder}
            isLoading={isLoading}
            onSortByChange={handleSortByChange}
            onSortOrderChange={handleSortOrderChange}
            currentLimit={limit}
            onLimitChange={handleLimitChange}
          />
        </HStack>

        <TableContainer>
          <Table minW="812px">
            <Thead>
              <Tr>
                <Th>Email</Th>
                <Th minW="320px">Unit/Org/Company</Th>
                <Th>Approved</Th>
                <Th>2FA</Th>
                <Th>Joined At (ET)</Th>
                <Th>Last Login (ET)</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {users &&
                !isLoading &&
                users.map((user) => {
                  const isApproved = user.accountStatus === 'approved';
                  const isPending = user.accountStatus === 'pending';
                  return (
                    <Tr key={user.id} color={isPending ? 'red.400' : 'white'}>
                      <Td lineHeight="10">{user.email}</Td>
                      <Td>
                        <EditOrgForm
                          initialOrg={user.organization}
                          userId={user.id}
                          onSubmit={handleOrgUpdate}
                        />
                      </Td>
                      <Td>
                        <Switch
                          isChecked={isApproved}
                          size="md"
                          onChange={() =>
                            handleStatusChange(user.id, user.accountStatus as StatusType)
                          }
                        />
                      </Td>
                      <Td>
                        <TwoFactorBadge
                          enabled={user.twoFactorEnabled || false}
                          enabledAt={user.twoFactorEnabledAt}
                          lastUsedAt={user.twoFactorLastUsedAt}
                        />
                      </Td>
                      <Td>{convertToEt(user.timeJoined, true)}</Td>
                      <Td>{user.lastLoggedIn ? convertToEt(user.lastLoggedIn, true) : 'N/A'}</Td>
                      <Td>
                        {user.twoFactorEnabled && (
                          <Button
                            size="sm"
                            colorScheme="red"
                            variant="ghost"
                            onClick={() => handleReset2FAClick(user.id, user.email)}
                          >
                            Reset 2FA
                          </Button>
                        )}
                      </Td>
                    </Tr>
                  );
                })}
              {isLoading && (
                <LoadingRows
                  limit={limit}
                  columns={[
                    { width: '160px' },
                    { width: '273px' },
                    { width: '72px' },
                    { width: '80px' },
                    { width: '120px' },
                    { width: '122px' },
                    { width: '100px' },
                  ]}
                  rowHeight="73px"
                />
              )}
            </Tbody>
          </Table>
        </TableContainer>
        <TablePagination
          currentPage={pagination.currentPage}
          hasNextPage={pagination.hasNextPage}
          isLoading={isLoading}
          onPrevPage={handlePrevPage}
          onNextPage={handleNextPage}
        />
      </VStack>
      <Reset2FAModal
        isOpen={isResetOpen}
        onClose={onResetClose}
        onConfirm={handleReset2FAConfirm}
        isLoading={isResetting}
        userEmail={selectedUser?.email || ''}
      />
    </VStack>
  );
};
